import { PanelState, State } from "@/shared/state/visualization";
import { PlotYAxis } from "@/shared/state/visualization/schema/v1";
import * as threeDSchema from "@/shared/state/visualization/schema/v1/three-d";

import { CanonicalDataType } from "../topics";

export enum LayoutAccessibility {
  Organization = "organization",
  User = "user",
}

export interface Requirement {
  topicMessagePath: string;
  dataType?: CanonicalDataType;
}

export interface Rule {
  id: string;
  requirements: Requirement[];

  /** Styles to apply to the panel when this rule matches data */
  style?: RuleStyle;
}

export type RuleStyle = Partial<{
  /** Color (HSLA, hex, or RGBA) to apply to the matched data */
  color: string;

  /** Line widths for plotted data */
  lineWidth: number;
  /** Point sizes for plotted data */
  pointRadius: number;
  /** Y axis for plotted data */
  yAxis: PlotYAxis;

  threeDComponents: {
    entityId: threeDSchema.EntityID;
    position?: ThreeDPositionStyle;
    mesh?: threeDSchema.Mesh;
    path?: threeDSchema.Path;
  };
}>;

/**
 * The styling & data mapping for a 3D position.
 *
 * This defines the topic data slots, along with the coordinate transformation & config presets.
 */
export interface ThreeDPositionStyle {
  transformation?: threeDSchema.PositionTransformation;
  basePreset?: threeDSchema.PositionBasePreset;
  forward: ThreeDPositionDataMapping;
  right: ThreeDPositionDataMapping;
  up: ThreeDPositionDataMapping;
}

/**
 * A 3d panel position's x, y, or z value.
 *
 * These will either contain constant values or topicdata.
 */
export type ThreeDPositionDataMapping =
  | { type: "topicData"; scale: number; topicDataIndex: number }
  | { type: "fixed"; scale: number; value: number };

export interface LayoutPanelState<TConfig = unknown>
  extends Omit<PanelState<unknown, TConfig>, "data"> {
  rules: string[];
}

export interface LayoutDefinition {
  panels: Record<string, LayoutPanelState>;
  layout: State["layout"];
  rules: Rule[];
}

export interface LayoutRecord {
  accessibility: LayoutAccessibility;
  created: string;
  created_by: string;
  layout_definition: LayoutDefinition;
  layout_id: string;
  modified: string;
  modified_by: string;
  name: string;
  org_id: string;
  schema_version: number;
  tags: string[];
}

export interface CreateLayoutRequest {
  accessibility: LayoutAccessibility;
  layout_definition: LayoutDefinition;
  name: string;
  schema_version: number;
  tags: string[];
}

export type UpdateLayoutRequest = Partial<{
  accessibility: LayoutAccessibility;
  layout_definition: LayoutDefinition;
  name: string;
  schema_version: number;
  tags: string[];
}>;

export function isLayoutRecord(value: unknown): value is LayoutRecord {
  // Good enough check of the core fields.
  return (
    value instanceof Object &&
    "layout_id" in value &&
    "name" in value &&
    "accessibility" in value &&
    "layout_definition" in value &&
    "schema_version" in value
  );
}
